import enTranslations from '../assets/languages/en-us.json';
import { loadCatalog } from '../i18n/catalog';
import { translate, type TranslateVars } from '../i18n/translate';
import {
  getLanguageMeta,
  type LanguageCode,
  type MessageCatalog,
} from '../i18n/types';

let activeCatalog: MessageCatalog = enTranslations;
let activeLanguage: LanguageCode = 'en';
/** Language the screen is showing. The picker reads this during render. */
let screenLanguage: LanguageCode = 'en';
const listeners = new Set<() => void>();

/**
 * Translated string. `{name}` values are filled from `vars`.
 * The key itself is returned when neither the active catalog nor English has it.
 */
export function t(key: string, vars?: TranslateVars): string {
  return translate(activeCatalog, enTranslations, key, vars);
}

/** English catalog, ignoring the language the screen is showing. */
export function tEnglish(key: string, vars?: TranslateVars): string {
  return translate(enTranslations, enTranslations, key, vars);
}

/** Catalog sentence before placeholders are filled. Used by rich text. */
export function messageTemplate(key: string): string {
  return activeCatalog[key] ?? enTranslations[key as keyof typeof enTranslations] ?? key;
}

export function getCurrentLanguage(): LanguageCode {
  return activeLanguage;
}

/** BCP-47 locale for number and date formatting. English is `en-US`. */
export function getCurrentLocale(): string {
  return getLanguageMeta(activeLanguage).locale;
}

/** Point `t()` at a catalog without notifying. App calls this before rendering a screen. */
export function syncActiveLanguage(code: LanguageCode, catalog: MessageCatalog): void {
  activeLanguage = code;
  activeCatalog = catalog;
  screenLanguage = code;
}

export function getScreenLanguage(): LanguageCode {
  return screenLanguage;
}

export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Switch the catalog and tell subscribers to redraw. */
export function publishLanguage(code: LanguageCode, catalog: MessageCatalog): void {
  syncActiveLanguage(code, catalog);
  listeners.forEach((listener) => listener());
}

export async function loadAndPublish(code: LanguageCode): Promise<MessageCatalog> {
  const catalog = await loadCatalog(code);
  publishLanguage(code, catalog);
  return catalog;
}

/**
 * Startup still calls this with the Genesys `langTag`. It does not change
 * the visible language: I18nProvider owns that, and only a chosen language
 * leaves English.
 */
export async function setLanguage(langTag: string): Promise<void> {
  void langTag;
}
